import React from 'react';
import { Badge } from '@repo/ui';
import type { ProcessingActionValue } from '../types';

/**
 * Mirrors `ProcessingActionLabels` on the backend
 * ([apps/api/src/modules/juniper/dto/juniper.dto.ts](../../../api/src/modules/juniper/dto/juniper.dto.ts)).
 * Keep these two in sync — same map, same wording.
 */
const PROCESSING_ACTION_LABELS: Record<ProcessingActionValue, string> = {
  send: 'Sending Email…',
  release: 'Releasing…',
  regenerate_pdf: 'Regenerating PDF…',
  reprocess: 'Reprocessing…',
  void: 'Voiding…',
  delete: 'Deleting…',
  mark_completed: 'Marking Completed…',
  refresh: 'Refreshing from Clio…',
  retry_last_event: 'Retrying Last Event…',
  consolidate: 'Consolidating…',
};

interface ProcessingActionBadgeProps {
  action?: ProcessingActionValue;
  state?: 'queued' | 'processing' | 'failed';
  error?: string;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Renders the in-flight bulk-action badge on a transaction row.
 *
 * - When `processingAction` is set and `processingState !== 'failed'`, shows a
 *   spinner + the action label (e.g. "Sending Email…"). Other users see the
 *   same badge and know not to re-trigger.
 * - When `processingState === 'failed'`, shows a sticky red badge with the
 *   captured `processingError`. The backend keeps this for ~24h via the
 *   `processingExpiresAt` TTL on the row.
 * - When neither is set, returns null.
 */
export const ProcessingActionBadge: React.FC<ProcessingActionBadgeProps> = ({
  action,
  state,
  error,
  size = 'sm',
}) => {
  if (state === 'failed') {
    return (
      <Badge variant="error" size={size} className="gap-1.5" title={error || 'Processing failed'}>
        <span aria-hidden>⚠</span>
        <span className="truncate max-w-[180px]">{error || 'Processing failed'}</span>
      </Badge>
    );
  }

  if (!action) return null;

  return (
    <Badge variant="warning" size={size} className="gap-1.5">
      <svg
        className="animate-spin h-3 w-3"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden
      >
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        />
      </svg>
      <span>{PROCESSING_ACTION_LABELS[action] || action}</span>
    </Badge>
  );
};
